import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import ExceptionHandler from './ExceptionHandler';
import Layout from './pages/Layouts';
import Guest from './pages/Layouts/Guest';
import IdleTimer from './components/IdleTimer';
import ScrollToTop from './pages/Shared/ScrollToTop';

import 'react-toastify/dist/ReactToastify.css';
import "./assets/css/style.module.css";

const NoMatch = lazy(() => import('./components/Materials/NoMatch'));
const Login = lazy(() => import('./pages/Guest/Login'));
const Forgot = lazy(() => import('./pages/Guest/Forgot'));
const TwoStepAuths = lazy(() => import('./pages/Guest/TwoStepAuths'));
const ResetPassword = lazy(() => import('./pages/Guest/ResetPassword'));
const Step1 = lazy(() => import('./pages/Quote/Step1'));
const Step2 = lazy(() => import('./pages/Quote/Step2'));
const Step3 = lazy(() => import('./pages/Quote/Step3'));
const Step4 = lazy(() => import('./pages/Quote/Step4'));
const Step5 = lazy(() => import('./pages/Quote/Step5'));
const Step6 = lazy(() => import('./pages/Quote/Step6'));
const Step7 = lazy(() => import('./pages/Quote/Step7'));
const MyAccount = lazy(() => import('./pages/Account/index'));
const ManageAgency = lazy(() => import('./pages/Account/ManageAgency'));
const BrokerEdit = lazy(() => import('./pages/Account/ManageAgency/BrokerEdit'));
const BrokerView = lazy(() => import('./pages/Account/ManageAgency/BrokerView'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const EditQuote = lazy(() => import('./pages/Quote/Edit'));
const Quotes = lazy(() => import('./pages/Insurance/Quotes'));
const Policies = lazy(() => import('./pages/Insurance/Policies'));
const QuoteView = lazy(() => import('./pages/Quote/View'));
const PolicyView = lazy(() => import('./pages/Policy/View'));
const Transaction = lazy(() => import('./pages/Transaction'));
const Renewals = lazy(() => import('./pages/Renewals'));
const Search = lazy(() => import('./pages/Search'));
const MTAEdit = lazy(() => import('./pages/Policy/MTA/index'));
const MTAView = lazy(() => import('./pages/Policy/MTA/View/index'));
const VersionCheck = lazy(() => import('./pages/Shared/VersionCheck'));

const SuspendPage = (element, fallback = <div />) => {
    return (
        <Suspense fallback={fallback}>
            {element}
        </Suspense>
    )
}

export default function App() {
    return (
        <BrowserRouter>
            <ScrollToTop />
            <ExceptionHandler>
                <VersionCheck />
                <IdleTimer />
                <Routes>
                    <Route element={<Guest />}>
                        <Route path="/setup-password/:hash" element={SuspendPage(<ResetPassword />)} />
                        <Route path="/reset-password/:hash" element={SuspendPage(<ResetPassword />)} />
                        <Route path="/forgot-password" element={SuspendPage(<Forgot />)} />
                        <Route path="/2fa-verification" element={SuspendPage(<TwoStepAuths />)} />
                        <Route path="/" element={SuspendPage(<Login />)} />
                    </Route>
                    <Route element={<Layout />}>
                        <Route path="/quote/view/:id" element={SuspendPage(<QuoteView />)} />
                        <Route path="/quote/edit/:id" element={SuspendPage(<EditQuote />)} />
                        <Route path="/quote/step07/:id" element={SuspendPage(<Step7 />)} />
                        <Route path="/quote/step06/:id" element={SuspendPage(<Step6 />)} />
                        <Route path="/quote/step05/:id" element={SuspendPage(<Step5 />)} />
                        <Route path="/quote/step04/:id" element={SuspendPage(<Step4 />)} />
                        <Route path="/quote/step03/:id" element={SuspendPage(<Step3 />)} />
                        <Route path="/quote/step02/:id" element={SuspendPage(<Step2 />)} />
                        <Route path="/quote/step01/:id" element={SuspendPage(<Step1 />)} />
                        <Route path="/quote/step01" element={SuspendPage(<Step1 />)} />
                        <Route path="/quote" element={SuspendPage(<Step1 />)} />
                        <Route path="/quotes" element={SuspendPage(<Quotes />)} />
                        <Route path="/policy/view/:id" element={SuspendPage(<PolicyView />)} />
                        <Route path="/policies" element={SuspendPage(<Policies />)} />
                        <Route path="/mta/edit/:id" element={SuspendPage(<MTAEdit />)} />
                        <Route path="/mta/view/:id" element={SuspendPage(<MTAView />)} />
                        <Route path="/my-profile" element={SuspendPage(<MyAccount />)} />
                        <Route path="/manage-agency/broker/edit/:id" element={SuspendPage(<BrokerEdit />)} />
                        <Route path="/manage-agency/broker/view/:id" element={SuspendPage(<BrokerView />)} />
                        <Route path="/manage-agency/broker/create" element={SuspendPage(<BrokerEdit />)} />
                        <Route path="/manage-agency" element={SuspendPage(<ManageAgency />)} />
                        <Route path="/dashboard" element={SuspendPage(<Dashboard />)} />
                        <Route path="/transaction" element={SuspendPage(<Transaction />)} />
                        <Route path="/renewals" element={SuspendPage(<Renewals />)} />
                        <Route path="/search" element={SuspendPage(<Search />)} />
                        <Route path="*" element={SuspendPage(<NoMatch />)} />
                    </Route>
                </Routes>
                <ToastContainer />
            </ExceptionHandler>
        </BrowserRouter>
    )
}